import Modal from "@/components/shared/modal";
import { useState, useEffect, Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { getPostsByTagIds } from "@/lib/api/post";
import CardWrapper from "./pictureCard/cardWrapper";
import Image from "next/image";

type SignInModalProps = {
  showSignInModal: boolean;
  setShowSignInModal: Dispatch<SetStateAction<boolean>>;
  // locale: {
  //   signInModalSignIn: string;
  //   signInModalSignInDescription: string;
  //   signInModalSignInButton: string;
  //   copy: string;
  //   copied: string;
  //   toolTip1: string;
  //   toolTip2: string;
  //   toolTip3: string;
  // };
  translations: any;
  toggleId: string;
  toggleName: string;
  selectedGenerator: string;
};

interface Post {
  _id: string;
  author: string;
  createdAt: string;
  imageUrl: string;
  videoUrl: string;
  thumbnailUrl: string;
  likes: number;
  model: string;
  tagGroups: string[];
  updatedAt: string;
  userLiked: boolean;
  views: number;
}

interface PageData {
  info: {
    count: number;
    next: string | null;
    pages: number;
    prev: string | null;
  };
  results: Post[];
}

const SignInModal = ({
  showSignInModal,
  setShowSignInModal,
  translations,
  toggleId,
  toggleName,
  selectedGenerator,
}: SignInModalProps) => {
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  // const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEndOfPosts, setIsEndOfPosts] = useState(false);

  // Function to fetch filtered posts by tagIds
  const fetchFilteredPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const tagIds = [toggleId]; // Assuming you want to use the _id as the tagId
      const limit = 20; // You can adjust the limit as needed
      const timespan = 0; // You can adjust the timespan as needed
      const images = 1;
      const views = 1;
      const likes = 1;
      const author = 1;
      const generator = 1;
      const generatorId = selectedGenerator; // Use the selectedGenerator prop

      const { response, error } = await getPostsByTagIds(
        tagIds,
        limit,
        timespan,
        images,
        views,
        likes,
        author,
        generator,
        generatorId,
      );

      if (error) {
        console.error("Error fetching filtered posts:", error);
      } else {
        const newPosts = response?.data.results; // Assuming response is the object you showed
        // console.log(newPosts);
        if (newPosts && newPosts.length === 0) {
          setIsEndOfPosts(true);
        } else if (newPosts) {
          // console.log(Array.isArray(newPosts)); // Should log true if newPosts is an array
          setFilteredPosts((prevPosts) =>
            Array.isArray(newPosts) ? [...prevPosts, ...newPosts] : prevPosts,
          );
        }
      }
    } catch (error) {
      console.error("Error fetching filtered posts:", error);
    } finally {
      setIsLoading(false);
    }
  }, [toggleId, selectedGenerator]); // Add other dependencies as needed

  useEffect(() => {
    if (showSignInModal) {
      fetchFilteredPosts();
    }
  }, [showSignInModal, fetchFilteredPosts]);

  return (
    <Modal showModal={showSignInModal} setShowModal={setShowSignInModal}>
      <div className="h-[70vh] w-full overflow-hidden border-0 border-[#3C3C3C] shadow-xl sm:border-[0.5px] md:max-w-3xl md:rounded-2xl">
        <div className="flex flex-wrap items-center justify-center border-b border-b-[0.5px] border-[#3C3C3C] bg-[#202124] px-6 py-6 text-center font-bold text-white md:px-16">
          {translations.toolTip1}
          {toggleName}
        </div>
        <div className="flex h-full flex-col space-y-4 overflow-y-auto bg-[#202124] sm:px-4 sm:pb-16 sm:pt-8 md:px-16">
          {isLoading ? (
            // <div tabIndex={0} className="pt-8 sm:pt-0 px-4 sm:px-0 font-bold text-white">
            //   {locale.toolTip2}
            // </div>
            <div tabIndex={0} className="mx-auto flex pt-8 sm:pt-0">
              <Image
                src={"https://cdn6.imgpog.com/loadingGIF.webp"}
                width={50}
                height={50}
                alt="Loading"
                className="m-auto"
                unoptimized
              />
            </div>
          ) : filteredPosts.length > 0 ? (
            <CardWrapper data={filteredPosts} />
          ) : (
            <div tabIndex={0} className="mx-auto pt-8 font-bold text-white sm:pt-0">
              {translations.toolTip3}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export function useSignInModal(
  translations: SignInModalProps["translations"],
  toggleId: SignInModalProps["toggleId"],
  toggleName: string,
  selectedGenerator: string,
) {
  const [showSignInModal, setShowSignInModal] = useState(false);

  const SignInModalCallback = useCallback(() => {
    return (
      <SignInModal
        showSignInModal={showSignInModal}
        setShowSignInModal={setShowSignInModal}
        translations={translations}
        toggleId={toggleId}
        toggleName={toggleName}
        selectedGenerator={selectedGenerator}
      />
    );
  }, [showSignInModal, setShowSignInModal, translations, toggleId, toggleName, selectedGenerator]);

  return useMemo(
    () => ({ setShowSignInModal, SignInModal: SignInModalCallback }),
    [setShowSignInModal, SignInModalCallback],
  );
}
